<script lang="ts" setup>
    import { onMounted, reactive, computed } from 'vue';
    import { ActionButtonStyle } from '../../lib/Types/Types';
    import { ButtonDisplaySize, ButtonDisplayStyle } from '../../Enums/UiEnums';
    import { AppScreens } from "../../Enums/ChoiceEnums";
    import { DSIAddInputs, DSIDetailType, DSIDetails} from '../../lib/Models/DSIModel';
    import { initPinia } from '../../lib/PiniaMgr';
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore' 
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import TextInput from '../../components/TextInput.vue';
    import * as ChoiceApi from '../../lib/ChoiceApi';

    const dsiHeaderClass = "dsi-header-width label-isalus-small pe-0";
    const dsiDataClass = "col label-isalus-small ps-0";
    const dsiAlertClass = `${dsiDataClass} fw-bold`;
    const dsiLinkClass = "col isalus-link label-isalus-small ps-0";

    const props = defineProps<{
        patientId: number,
        identifier: string,
        details: DSIDetails,
        height?: number,
    }>(
    );

    const formData = reactive<{
        height: number,
        dismissalReason?: string,
        canDismiss: boolean
    }>({
        height: props.height || 200,
        canDismiss: false
    });

    const privilegesStore = usePrivilegesStore(initPinia());

    const emit = defineEmits<{
        (e: "onClose" ): void,
        (e: "onDismiss", identifier: string, dismissed: boolean): void
    }>();

    onMounted(async () => {
        getPrivileges();
    });

    const heigtOffset = computed<number>(() => formData.canDismiss ? 95 : 40);
    const controlHeightStyle = computed<string>(() => `overflow-y: auto; overflow-x: hidden; height: ${formData.height - heigtOffset.value}px`);
    const isDismissed = computed<boolean>(() => props.details.dismissed ?? false);
    const displayAlert = computed<string>(() => `${isDismissed.value ? "(DISMISSED) " : ""}${props.details.alert}`);
    const dismissButtonText = computed<string>(() => isDismissed.value ? "Undismiss" : "Dismiss");
    const dismissButtonSpinText = computed<string>(() => isDismissed.value ? "Undismissing" : "Dismissing");
    const dismissButtonSpinTooltip = computed<string>(() => isDismissed.value ? "Undismiss the alert" : "Dismiss the alert");
    const displayType = computed<string>(() => props.details.type || "Recommendation");
    const displayDetailType = computed<boolean>(() => props.details.detailType === DSIDetailType.URL || props.details.detailType === DSIDetailType.eDocumentId);
    const triggers = computed<string>(() => {
        const triggerList = [];
        if (props.details.triggerRace) triggerList.push("Race");
        if (props.details.triggerEthnicity) triggerList.push("Ethnicity");
        if (props.details.triggerLanguage) triggerList.push("Language");
        if (props.details.triggerSex) triggerList.push("Sex");
        if (props.details.triggerOrientation) triggerList.push("Sexual Orientation");
        if (props.details.triggerGenderIdentity) triggerList.push("Gender Identity");
        if (props.details.triggerBirthDate) triggerList.push("Date of Birth");
        return triggerList.join(", ");
    });
    const displayTriggers = computed<boolean>(() => triggers.value.length > 0);

    function onLoadGuideline() {
        window.open(props.details.guideline, '_blank');
    }

    function onLoadDetails() {
        if (props.details.detailType === DSIDetailType.URL) {
            window.open(props.details.detailUrl, '_blank');
        }
        else if (props.details.detailType === DSIDetailType.eDocumentId) {
            window.open(`/OfficeMD/api/eDocument/Preview/${props.details.detailId}`, '_blank');
        }
    }

    async function getPrivileges() {
        await privilegesStore.getScreens([AppScreens.DSIFeedback]);
        formData.canDismiss = privilegesStore.screensWithWritePrivileges[AppScreens.DSIFeedback];        
    }

    async function onDismiss() {
        const inputs = {
            patientId: props.patientId,
            identifier: props.identifier,
            reason: formData.dismissalReason,
            dismissed: !isDismissed.value
        } as DSIAddInputs;
        const result = await ChoiceApi.DSI.add(inputs);
        if (result) {
            emit("onDismiss", props.identifier, !isDismissed.value);
        }
    }

    async function onClose() {
        emit("onClose");
    }   

</script>

<style>
    .dsi-header-width {
        width: 120px;
    }
</style>

<template>
    <div :style="controlHeightStyle">
        <div class="row mt-1">
            <div :class="dsiHeaderClass">
                Alert
            </div>
            <div :class="dsiAlertClass">
                {{ displayAlert }}
            </div>
        </div>
        <div class="row" v-if="props.details.citation">
            <div :class="dsiHeaderClass">
                Citation
            </div>
            <div :class="dsiDataClass">
                {{ props.details.citation }}
            </div>
        </div>
        <div class="row">
            <div :class="dsiHeaderClass">
                DSI Type
            </div>
            <div :class="dsiDataClass">
                {{ displayType }}
            </div>
        </div>
        <div class="row" v-if="props.details.developer">
            <div :class="dsiHeaderClass">
                Developer
            </div>
            <div :class="dsiDataClass">
                {{ props.details.developer }}
            </div>
        </div>
        <div class="row" v-if="props.details.fundingSource">
            <div :class="dsiHeaderClass">
                Funding Source
            </div>
            <div :class="dsiDataClass">
                {{ props.details.fundingSource }}
            </div>
        </div>
        <div class="row" v-if="props.details.release">
            <div :class="dsiHeaderClass">
                Release
            </div>
            <div :class="dsiDataClass">
                {{ props.details.release }}
            </div>
        </div>
        <div class="row" v-if="props.details.guideline">
            <div :class="dsiHeaderClass">
                Guideline
            </div>
            <div @click="onLoadGuideline" :class="dsiLinkClass">
                Click to View
            </div>
        </div>
        <div class="row" v-if="displayDetailType">
            <div :class="dsiHeaderClass">
                Details
            </div>
            <div @click="onLoadDetails" :class="dsiLinkClass">
                Click to View
            </div>
        </div>
        <div class="row" v-if="displayTriggers">
            <div :class="dsiHeaderClass">
                Triggers
            </div>
            <div :class="dsiDataClass">
                {{ triggers }}
            </div>
        </div>
    </div>
    <div class="row mt-2" v-if="formData.canDismiss">
        <div :class="dsiHeaderClass">
            Dismissal Reason
        </div>
        <div :class="dsiDataClass">
            <TextInput 
                :rows=2
                :cssFramework="'vanilla'"  
                v-model:textValue="formData.dismissalReason" 
                :hideTranscribe="true"
                placeholder="Enter dismissal reason/feedback" />
        </div>
    </div>
    <div class="row mt-2">
        <div class="col">
            <ActionButtonV2 
                v-if="formData.canDismiss"
                :action="ActionButtonStyle.Save"
                :text="dismissButtonText"
                :spinText="dismissButtonSpinText"
                :toolTip="dismissButtonSpinTooltip"
                :size="ButtonDisplaySize.Small" 
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="onDismiss"/>   
        </div> 
        <div class="col-auto">
            <ActionButtonV2 
                :action="ActionButtonStyle.Close"
                text="Close"
                spinText="Closing"
                toolTip="Close the Decision Support Intervention Details."
                :size="ButtonDisplaySize.Small" 
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="onClose"/>   
        </div> 
    </div>
</template>

